//layout.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import Sidebar from "./Sidebar";
import { useAuth } from "../auth/AuthContext";
import { useBodyScrollLock } from "../utils/useBodyScrollLock";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useBodyScrollLock(showLogoutConfirm);

  const handleLogout = async () => {
    const redirected = await logout();
    // If logout() already kicked off a full-page redirect to the SSO
    // logout URL, don't also navigate client-side — let the browser go.
    if (!redirected) {
      navigate("/logged-out", { replace: true });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "var(--paper)",
      }}
    >
      <Sidebar onRequestLogout={() => setShowLogoutConfirm(true)} />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <main
          style={{
            flex: 1,
            padding: "32px 40px 64px",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          {children}
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17, 24, 39, 0.4)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 24,
          }}
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: 24,
              padding: "32px 32px 24px 32px",
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "1px solid var(--ink-100)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "#fff1f2",
                color: "#e11d48",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 20,
                boxShadow: "0 0 0 8px rgba(225, 29, 72, 0.05)",
              }}
            >
              <LogOut size={28} strokeWidth={2} style={{ marginLeft: -2 }} />
            </div>

            <h3
              style={{
                margin: "0 0 10px 0",
                color: "var(--ink-900)",
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              Ready to leave?
            </h3>

            <p
              style={{
                margin: "0 0 32px 0",
                color: "var(--ink-500)",
                fontSize: 15,
                lineHeight: 1.5,
                maxWidth: "90%",
              }}
            >
              You are about to sign out of your account. You will need to log
              back in to access the console.
            </p>

            <div style={{ display: "flex", gap: 12, width: "100%" }}>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: 12,
                  border: "1px solid var(--ink-200)",
                  background: "#ffffff",
                  color: "var(--ink-700)",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--paper-2)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#ffffff")
                }
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: 12,
                  border: "none",
                  background: "#e11d48",
                  color: "#ffffff",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 12px rgba(225, 29, 72, 0.2)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#be123c";
                  e.currentTarget.style.boxShadow =
                    "0 6px 16px rgba(225, 29, 72, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#e11d48";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(225, 29, 72, 0.2)";
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}